import { InputAdornment, TextField, Box } from '@mui/material';
import { BaseTheme, IconButton } from 'design-system';
import { useEffect, useRef, useState } from 'react';
import { BsTrash } from 'react-icons/bs';
import { FaMinus, FaPlus } from 'react-icons/fa';

interface NumberStepperProps {
  label?: string;
  amount: number;
  min: number;
  max?: number;
  stepSize?: number;
  disabled?: boolean;
  error?: boolean;
  width?: number;
  onChange: (amount: number) => void;
  onBlur?: () => void;
  onRemove?: () => void;
  onValidate?: (oldAmout: number, amount: number) => boolean;
}

const VALIDATE_TIMEOUT = 1000;
const inlineButtonStyle = (theme: BaseTheme) => ({
  background: theme.palette.common.white,
  boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12);',
});

const NumberStepper = ({
  label,
  amount,
  min,
  max,
  onValidate,
  stepSize = 1,
  width = 80,
  disabled,
  error = false,
  onChange,
  onBlur,
  onRemove,
}: NumberStepperProps) => {
  const [currentAmount, setCurrentAmount] = useState(amount);
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setCurrentAmount(amount);
  }, [amount]);

  const handleUpdateValue = (oldAmount: number, newAmount: number) => {
    if (onValidate && !onValidate(oldAmount, newAmount)) {
      return;
    }

    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }

    setCurrentAmount(newAmount);

    const isOverMax = max && newAmount > max;
    const isUnderMin = newAmount < min;
    if (isOverMax || isUnderMin) {
      timeoutId.current = setTimeout(() => {
        setCurrentAmount(oldAmount);
        onChange(oldAmount);
      }, VALIDATE_TIMEOUT);
      return;
    }

    onChange(newAmount);
  };

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          '& > button': {
            padding: theme.spacing(1),
          },
        })}
      >
        <IconButton
          sx={{ inlineButtonStyle }}
          onClick={
            currentAmount <= min
              ? () => onRemove?.()
              : () => {
                  const newAmount = Math.ceil(currentAmount - stepSize);
                  setCurrentAmount(newAmount);
                  onChange(newAmount);
                }
          }
          disabled={(currentAmount <= min && onRemove === undefined) || disabled}
          size="large"
        >
          {currentAmount <= min && onRemove !== undefined ? <BsTrash size={14} /> : <FaMinus size={14} />}
        </IconButton>
        <TextField
          disabled={disabled}
          error={error}
          variant="outlined"
          margin="dense"
          placeholder="0"
          sx={(theme) => ({
            width: 90,
            '& > div': {
              paddingRight: theme.spacing(0.5),
            },
            '& > div:first-of-type': {
              background: theme.palette.common.white,
            },
            '& input': {
              textAlign: 'right',
              paddingLeft: theme.spacing(0.25),
            },
          })}
          style={{ width }}
          value={currentAmount === 0 ? '' : currentAmount.toString()}
          type="text"
          onBlur={onBlur}
          onChange={(e) => {
            if (!e.target.value) return;

            const newAmount = Number.parseInt(e.target.value, 10) || 0;

            if (onValidate && !onValidate(currentAmount, newAmount)) {
              return;
            }

            handleUpdateValue(amount, newAmount);
          }}
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              pattern: '[0-9]*',
            },
            input: label
              ? {
                  endAdornment: <InputAdornment position="end">{label}</InputAdornment>,
                }
              : {},
          }}
        />
        <IconButton
          sx={{ inlineButtonStyle }}
          disabled={(max !== undefined && max === currentAmount) || disabled}
          onClick={() => {
            const newAmount = Math.floor(currentAmount + stepSize);

            if (onValidate && !onValidate(currentAmount, newAmount)) {
              return;
            }

            setCurrentAmount(newAmount);
            onChange(newAmount);
          }}
          size="large"
        >
          <FaPlus size={14} />
        </IconButton>
      </Box>
    </>
  );
};

export default NumberStepper;
